import { useCallback } from "react";
import i18n from "i18next";

/**
 * 上课模式文本清洗：去除 mermaid 块、markdown 表格、<voice> 标签、A2UI 块、代码块，保留正文
 */
export function extractClassModeText(markdown) {
  return markdown
    .replace(/```mermaid[\s\S]*?```/g, "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/^\|.*\|$/gm, "")
    .replace(/^\|[-:\s|]+\|$/gm, "")
    .replace(/<voice>[\s\S]*?<\/voice>/g, "")
    .replace(/<state\s+name=["'][^"']+["']\s*>[\s\S]*?<\/state>/gi, "")
    .replace(/:::(?:choice-group|button|tip|chart)[\s\S]*?:::/g, "")
    .replace(/:::(?:input|progress)[^\n]*/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const useWsMessageHandlers = ({
  messages,
  setMessages,
  setError,
  sessionId,
  setIsMessageInputEnabled,
  setIsWaitingResponse,
  setIsLoading,
  showNotification,
  setIsProcessing,
  setExperts,
  processExpertsData,
  expertSearchTerm,
  selectedExpertCategory,
  parseExpertCategory,
  getExpertCategories,
  setExpertDialogContent,
  setIsExpertDialogReady,
  setAgents,
  setWelcomeMessage,
  parseMicroSceneData,
  setMicroSceneBubbles,
  isRestoringArchiveRef,
  digitalHumanGlobalEnabledRef,
  isExpertServiceAvailableRef,
  classModeRef,
  setCurrentExpertName,
  setCurrentSpeechText,
  setDigitalHumanOpen,
  currentInitializingExpertRef,
  setCurrentFileExpertName,
  setCurrentFileExpertText,
  setFileExpertDigitalHumanOpen,
  setLastMessageType,
  setProjectState,
  logAction,
  handleNewNotification,
  inputRef,
}) => {
  const updateProjectState = useCallback((newState) => {
    if (newState == "new_requirement") {
      setProjectState("新需求");
    } else if (newState) {
      setProjectState(newState);
    }
  }, [setProjectState]);

  return useCallback((data) => {
    if (data.type === "error") {
      console.error("📨 收到后端错误消息:", data.message);
      if (data.message && data.message.includes("训练流程已停止退出")) {
        console.error("⚠️ 工作流已停止:", data.message);
        setError({
          message: data.message,
          timestamp: Date.now(),
          sessionId: sessionId,
        });
        setIsMessageInputEnabled(false);
        setIsWaitingResponse(false);
        setIsLoading(false);
      } else if (data.message && data.message.includes("项目存档恢复到前端")) {
        console.log("✅ 存档恢复完成:", data.message);
        isRestoringArchiveRef.current = false;
        setIsMessageInputEnabled(true);
        setIsLoading(false);
        setIsWaitingResponse(false);
        // 🔧 在所有存档数据加载完成后显示恢复完成通知
        showNotification("存档恢复完成，您现在可以继续与系统交互", "success");
      } else if (data.message && data.message.includes("存档")) {
        console.log("恢复存档失败:", data.message);
        setIsLoading(false);
        setIsWaitingResponse(false);
        showNotification(data.message, "error");
      } else {
        setError({
          message: data.message,
          timestamp: Date.now(),
          sessionId: sessionId,
        });
        // 可恢复类错误（如 claw 空指令、参数校验失败）：已发 humaninput 后曾关闭输入并进入「思考中」，
        // 必须在收到 error 时恢复，否则会一直卡在 isWaitingResponse / 输入禁用。
        setIsWaitingResponse(false);
        setIsMessageInputEnabled(true);
        setIsProcessing(false);
        setIsLoading(false);
      }
      return true;
    }

    if (data.type === "expert_error") {
      console.log("收到专家错误消息:", data);
      const processingMessageIndex = messages.findIndex(
        (msg) => msg.role === "Expert" && msg.isProcessing === "true"
      );

      if (processingMessageIndex !== -1) {
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          newMessages[processingMessageIndex] = {
            type: "Expert",
            role: "Expert",
            context: data.content,
            agentName: data.agentName || "专家",
            isError: true,
            timestamp: new Date().toISOString(),
          };
          return newMessages;
        });
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            type: "Expert",
            role: "Expert",
            context: data.content,
            agentName: data.agentName || "专家",
            isError: true,
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      setIsWaitingResponse(false);
      setIsMessageInputEnabled(true);
      setIsProcessing(false);
      return true;
    }

    if (data.type === "expert_info") {
      console.log("收到专家信息:", data.experts);
      const processedExperts = processExpertsData(data.experts || []);
      setExperts(processedExperts);

      // 🔧 流程切换后 expert_info 可能为空（当前流程数据库未配置专家）。
      // 旧逻辑：length==0 时 expertDialogContent/isExpertDialogReady 完全不更新，
      //        于是面板仍保留上一个流程的专家，产生"切了流程但专家面板不变"的 bug。
      // 新逻辑：无论列表是否为空都显式更新状态，让 UI 进入"该流程暂无专家"的空态。
      if (processedExperts.length > 0) {
        const categories = getExpertCategories(processedExperts);
        const filteredExperts = processedExperts.filter((expert) => {
          const matchesSearch =
            !expertSearchTerm ||
            expert.display_name?.toLowerCase().includes(expertSearchTerm.toLowerCase()) ||
            expert.description?.toLowerCase().includes(expertSearchTerm.toLowerCase());

          const category = parseExpertCategory(expert);
          const matchesCategory =
            selectedExpertCategory === "全部" ||
            category.base_category === selectedExpertCategory;

          return matchesSearch && matchesCategory;
        });

        setExpertDialogContent({
          experts: processedExperts,
          categories,
          filteredExperts,
        });
      } else {
        console.warn("[expert_info] 收到空专家列表，清空专家面板（请检查 ybg_experts 表中当前流程是否配置了 expert_group 行）");
        setExpertDialogContent({
          experts: [],
          categories: ['全部'],
          filteredExperts: [],
        });
      }
      setIsExpertDialogReady(true);
      return true;
    }

    if (data.type === "agent_info") {
      setAgents(data.agents);

      // 根据当前语言选择字段
      const isZhCN = !i18n.language || i18n.language === 'zh-CN';
      const t = i18n.t.bind(i18n);
      const getName = (agent) => (!isZhCN && agent.name_en) ? agent.name_en : agent.name;
      const getDesc = (agent) => (!isZhCN && agent.description_en) ? agent.description_en : (agent.description || '');

      const headerName = t('welcome_table_header_name');
      const headerDesc = t('welcome_table_header_desc');
      let agentInfoString = `| ${headerName} | ${headerDesc} | ${headerName} | ${headerDesc} |\n| --- | --- | --- | --- |\n`;
      for (let i = 0; i < data.agents.length; i += 2) {
        const agent1 = data.agents[i];
        const agent2 = data.agents[i + 1];
        if (agent2) {
          agentInfoString += `| ${getName(agent1)} | ${getDesc(agent1)} | ${getName(agent2)} | ${getDesc(agent2)} |\n`;
        } else {
          agentInfoString += `| ${getName(agent1)} | ${getDesc(agent1)} | | |\n`;
        }
      }
      setWelcomeMessage({
        type: "AI",
        role: "AI",
        agentName: "Team Leader",
        context: `${t('welcome_title')}
${agentInfoString}
**${t('welcome_steps_title')}**

• ${t('welcome_step1')}

• ${t('welcome_step2')}

${t('welcome_footer')}`,
        timestamp: new Date().toISOString(),
      });
      return true;
    }

    if (data.type === "Micro_scene" || (data.type === "MsgtoHuman" && data.role === "Micro_scene")) {
      const newScenes = parseMicroSceneData(data.context);
      setMicroSceneBubbles((prevBubbles) => [...prevBubbles, ...newScenes]);
      return true;
    }

    if (data.type === "msg" || data.type === "MsgtoHuman" || data.type === "StreamingMsgtoHuman") {
      if (data.role === "Draft") {
        const diffData = JSON.parse(data.context);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            type: "Draft",
            role: "Draft",
            context: diffData,
            agentName: data.agentName,
          },
        ]);
      } else if (data.role !== "Micro_scene") {
        const isErrorMessage = data.isError === "true";
        const isProcessingMessage = data.isProcessing === "true";
        const isStreamingDone = data.isStreamingDone === "true";
        const messageData = {
          ...data,
          isError: isErrorMessage,
          isProcessing: isProcessingMessage,
        };
        if (messageData.role === "Human" || messageData.sendform === "Human") {
          messageData.type = "human";
        }

        if (data.type === "StreamingMsgtoHuman") {
          setMessages((prevMessages) => {
            const messageID = data.messageID;
            const existingIndex = prevMessages.findIndex(
              (msg) => msg.messageID === messageID
            );

            if (existingIndex >= 0) {
              const updatedMessages = [...prevMessages];
              updatedMessages[existingIndex] = {
                ...updatedMessages[existingIndex],
                context:
                  (updatedMessages[existingIndex].context || "") + data.context,
                timestamp: data.timestamp,
              };
              return updatedMessages;
            }
            return [
              ...prevMessages,
              {
                ...messageData,
                messageID: messageID,
                isStreaming: true,
              },
            ];
          });
        } else if (
          data.type === "MsgtoHuman" &&
          data.role === "FinalDoc" &&
          data.isHuman === "false"
        ) {
          // skip
        } else {
          // 流式完成消息仅用于触发数字人，不重复插入聊天气泡
          if (isStreamingDone && data.messageID) {
            // 流式完成：将消息类型改为 MsgtoHuman 以启用 A2UI 交互
            setMessages((prevMessages) => {
              const idx = prevMessages.findIndex(
                (msg) => msg.messageID === data.messageID
              );
              if (idx >= 0) {
                const existingContext = prevMessages[idx].context || "";
                const newContext = data.context || "";
                // 若气泡内容为空（纯 tool_use 轮次产生的空气泡），直接移除
                if (!existingContext.trim() && !newContext.trim()) {
                  const updated = [...prevMessages];
                  updated.splice(idx, 1);
                  return updated;
                }
                const updated = [...prevMessages];
                updated[idx] = {
                  ...updated[idx],
                  isStreaming: false,
                  type: "MsgtoHuman",
                };
                return updated;
              }
              return prevMessages;
            });
          } else if (!isStreamingDone) {
            setMessages((prevMessages) => [...prevMessages, messageData]);
          }

          if (
            messageData.agentName &&
            (messageData.agentName.includes("导师") ||
              messageData.agentName.includes("辅导员") ||
              messageData.agentName.toLowerCase().includes("mentor") ||
              messageData.agentName.toLowerCase().includes("counselor"))
          ) {
            if (isRestoringArchiveRef.current) {
              console.log("⏸️ 存档恢复中，跳过数字人触发:", messageData.agentName);
            } else if (!digitalHumanGlobalEnabledRef.current) {
              console.log("🔇 数字人全局开关已关闭，跳过触发:", messageData.agentName);
            } else if (isExpertServiceAvailableRef.current) {
              setCurrentExpertName(messageData.agentName);
              // 上课模式：读全文（去除 mermaid/表格/voice标签/A2UI块）；否则用后端提取的 voiceText
              let speechText;
              if (classModeRef.current) {
                speechText = messageData.speechText || extractClassModeText(messageData.context || "");
              } else {
                speechText = messageData.voiceText ||
                  (messageData.context || "")
                    .replace(/<state\s+name=["'][^"']+["']\s*>[\s\S]*?<\/state>/gi, "")
                    .replace(/:::(?:choice-group|button|tip|chart)[\s\S]*?:::/g, "")
                    .replace(/:::(?:input|progress)[^\n]*/g, "")
                    .trim();
              }
              setCurrentSpeechText(speechText);
              setDigitalHumanOpen(true);
            }
          }

          if (
            messageData.agentName &&
            (messageData.context === "收到，我思考一下，结果稍后出现在右侧且可以多轮交流 ->>>" ||
              messageData.context === "Received, let me think. Results will appear on the right panel for multi-turn conversation ->>>")
          ) {
            if (isRestoringArchiveRef.current) {
              console.log("⏸️ 存档恢复中，跳过专家预初始化:", messageData.agentName);
            } else if (!digitalHumanGlobalEnabledRef.current) {
              console.log("🔇 数字人全局开关已关闭，跳过预初始化:", messageData.agentName);
            } else if (isExpertServiceAvailableRef.current) {
              if (currentInitializingExpertRef.current !== messageData.agentName) {
                currentInitializingExpertRef.current = messageData.agentName;
                setCurrentFileExpertName(messageData.agentName);
                setCurrentFileExpertText("");
                setFileExpertDigitalHumanOpen(true);
              }
            }
          }

          if (isErrorMessage) {
            setIsWaitingResponse(false);
            setIsMessageInputEnabled(true);
            setIsProcessing(false);
          }
        }
      }

      setLastMessageType(data.type);

      if (data.project_state) {
        updateProjectState(data.project_state);
      }
      logAction("RECEIVED_MESSAGE", {
        messageType: data.type,
        role: data.role,
        projectState: data.project_state || null,
      });

      setIsLoading(false);
      setIsWaitingResponse(false);
      setIsMessageInputEnabled(true);
      setIsProcessing(false);

      if (inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
      return true;
    }

    if (data.type === "server_keepalive") {
      console.log("💓 收到服务器保活消息:", {
        timestamp: data.timestamp,
        time: new Date(data.timestamp * 1000).toLocaleTimeString(),
        sessionId: data.session_id,
      });
      return true;
    }

    if (data.type === "notification") {
      console.log("🔔 收到通知推送:", data.data);
      handleNewNotification(data.data);
      return true;
    }

    return false;
  }, [
    classModeRef,
    digitalHumanGlobalEnabledRef,
    expertSearchTerm,
    getExpertCategories,
    handleNewNotification,
    inputRef,
    isExpertServiceAvailableRef,
    isRestoringArchiveRef,
    logAction,
    messages,
    parseExpertCategory,
    parseMicroSceneData,
    processExpertsData,
    selectedExpertCategory,
    sessionId,
    setAgents,
    setCurrentExpertName,
    setCurrentFileExpertName,
    setCurrentFileExpertText,
    setCurrentSpeechText,
    setDigitalHumanOpen,
    setError,
    setExperts,
    setExpertDialogContent,
    setFileExpertDigitalHumanOpen,
    setIsExpertDialogReady,
    setIsLoading,
    setIsMessageInputEnabled,
    setIsProcessing,
    setIsWaitingResponse,
    setLastMessageType,
    setMessages,
    setMicroSceneBubbles,
    setProjectState,
    setWelcomeMessage,
    showNotification,
    updateProjectState,
    currentInitializingExpertRef,
  ]);
};

export default useWsMessageHandlers;
